<template>
	<AppModal
		v-if="isOpen"
		ref="modal"
		class="group"
		:class="{ 'dropping-files': isOverDropZone }"
		@hide-modal="isOpen = false"
	>
		<template #header>
			{{ splitHdnValue(doc.documentType)[1] }}
			<span
				v-if="doc.documentTypeDescription"
				class="text-gray-500 text-sm font-normal ml-2"
			>
				{{ doc.documentTypeDescription }}
			</span>
		</template>

		<div class="flex flex-col gap-2">
			<input
				:id="fileUploadInputId"
				ref="file-upload-input"
				type="file"
				class="hidden"
				:accept="acceptedMimeTypes.join(',')"
				@change="onFileInputChange"
			/>
			<label :for="fileUploadInputId" class="cursor-pointer">
				<div
					class="border rounded-sm border-dashed hover:border-gray-400 bg-gray-50 group-[.dropping-files]:outline-green-500 group-[.dropping-files]:outline-2 group-[.dropping-files]:outline-dashed group-[.dropping-files]:-outline-offset-2 group-[.dropping-files]:bg-green-50 p-4 m-2 mb-0 flex items-center justify-center gap-2 text-gray-400 hover:text-gray-500"
				>
					<AppIcon
						class="text-xl group-[.dropping-files]:text-green-500"
						name="upload"
					/>
					<span class="text-sm group-[.dropping-files]:text-green-600">
						Bestand uploaden
					</span>
				</div>
			</label>
			<span class="text-gray-500 text-sm self-center">
				of kies een bestand:
			</span>
			<ul>
				<li
					v-for="docToPick in docsToPick"
					:key="docToPick.id"
					class="border-t flex gap-2 justify-between px-4 py-2"
				>
					<div class="flex flex-col overflow-hidden">
						<span class="truncate text-ellipsis">{{ docToPick.filename }}</span>
						<span v-if="docToPick.creationDate" class="text-gray-400 text-sm">
							{{ formatDate(docToPick.creationDate) }}
						</span>
					</div>
					<AppButton
						v-if="docToPick.id !== linkedDoc?.id"
						variant="secondary"
						class="self-center"
						@click="pickExistingDoc(docToPick)"
					>
						Koppelen
					</AppButton>
					<AppButton
						v-else
						variant="success"
						icon="link"
						class="self-center"
						disabled
					>
						Gekoppeld
					</AppButton>
				</li>
			</ul>
		</div>
	</AppModal>
</template>

<script setup lang="ts">
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { formatDate } from "@/helpers/uiHelpers";
import { acceptedMimeTypes } from "@/helpers/fileHelpers";
import { useTemplateRef } from "vue";
import AppIcon from "../AppIcon.vue";
import AppModal from "../AppModal.vue";
import { useDropZone } from "@vueuse/core";
import type {
	DossierDocument,
	HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";

defineProps<{
	doc: HdnRequestedDocument;
	docsToPick: DossierDocument[];
	linkedDoc?: DossierDocument;
}>();
const emit = defineEmits<{
	picked: [doc: DossierDocument];
	dropped: [file: File];
}>();
const isOpen = defineModel<boolean>();

const modal = useTemplateRef<InstanceType<typeof AppModal>>("modal");
const { isOverDropZone } = useDropZone(() => modal.value?.modalBody, {
	multiple: false,
	onDrop: (files) => {
		const [file] = files ?? [];
		if (!file) {
			return;
		}

		emit("dropped", file);
		isOpen.value = false;
	},
});

const fileUploadInputId = crypto.randomUUID();
function onFileInputChange(e: Event) {
	const [file] = (e.target as HTMLInputElement).files ?? [];
	if (!file) {
		return;
	}

	emit("dropped", file);
	isOpen.value = false;
}

function pickExistingDoc(doc: DossierDocument) {
	emit("picked", doc);
}
</script>
